/**
 * The LV's one theme builder + brand asset loader. Both themed entry points —
 * the shell (LvShell) and the sign-in gate (LoginGate) — build their MUI
 * theme here at the SAME light/dark policy + learner choice (useBrandMode),
 * so a deployment's brand lands IDENTICALLY on the first page a learner sees
 * and on the app behind it.
 *
 * THE THREE-STAGE DERIVATION MODEL (branding expansion v2, 2026-10-06 —
 * Isobel: keep these concerns separate):
 *
 *   Stage 1 · BRAND PALETTE — the customer's three brand colours as resolved
 *     by lvConfig: stored values win; unset secondary/accent derive from the
 *     resolved primary (darken 0.3 / lighten 0.2 — the ratified documented
 *     defaults, Q1 ruling 2026-10-14). Everything downstream consumes the
 *     three resolved hexes; nothing downstream cares whether they were stored
 *     or derived.
 *   Stage 2 · LIGHT/DARK PALETTE DERIVATION — the brand palette becomes a
 *     mode-aware MUI palette: primary/secondary through createTheme's own
 *     augmentation (unchanged from v1), accent as a dedicated custom token,
 *     the highlight token, and the action.selected tint derived from the
 *     active-role colour against the mode's background.
 *   Stage 3 · CONTRAST/ACCESSIBILITY TREATMENT — contrastText per brand
 *     colour (MUI's exact recipe), and the focus ring: resolved focus-role
 *     colour, adjusted when needed to a 3:1 floor against the mode's
 *     background, at the system width/offset (2px/2px — fixed tokens).
 *
 * Parity contract (D5): with none of the 14 v2 keys stored, rendered output
 * matches the pre-expansion theme EXCEPT the ratified D5 deltas — the
 * palette.secondary slot's new owner and the derived secondary/accent values
 * — which the parity harness records as expected deltas (see
 * __golden__/theme-parity.expected-deltas.json). Every component override
 * below is emitted CONDITIONALLY so stock-rendered themes stay byte-stable.
 *
 * What the builder owns:
 *   shape.borderRadius   brand.radius (sharp 0 / rounded 8 / pill 16)
 *   palette.primary      brand.primary → legacy theme.colors[0] → default
 *   palette.secondary    brand.secondary (unset = derived from primary)
 *   palette.accent       brand.accent (unset = derived from primary) — the
 *                        dedicated accent token (custom palette slot, fully
 *                        augmented post-build via theme.palette.augmentColor)
 *   palette.highlight    highlight-role token (source: brand.highlightSource)
 *   action.selected      tinted from the active-role colour (alpha of the
 *                        resolved active source at MUI's stock opacities)
 *   typography           brand body/heading fonts (v3: 4-slot stacks —
 *                        custom library family → Mac fallback → PC fallback →
 *                        generic category); base fontSize (v3 accessible px
 *                        wins over the semantic small/medium/large →
 *                        13/14/16px, D6 candidates); body weight; heading
 *                        weight (default 600, ratified 2026-10-06 —
 *                        admin-tunable via brand.fontWeightHeading) with
 *                        per-header (h1–h4) size/weight overrides from
 *                        brand.headings
 *   components           UI-role mappings (MuiLink, MuiButton, MuiChip,
 *                        MuiBadge), emitted only when a role's resolved colour
 *                        differs from what stock MUI would render; the global
 *                        :focus-visible ring (system contract)
 *   shadows              elevation recipe (flat = none / subtle = stock /
 *                        elevated = one level up, D6 candidate)
 *
 * useBrandAssets loads the webfonts the resolved brand needs — the Google
 *  Fonts families (one combined stylesheet, display=swap, requesting exactly
 *  the resolved weight set in normal + italic — brandFontWeightSet) and any
 *  uploaded custom fonts (one @font-face per family, single file: every other
 *  weight/style synthesizes — the documented custom-font limitation) — and
 *  points the favicon at the brand logo while the page is up. Effects are
 *  keyed off the config object:
 *  no brand rows → no network requests, no DOM touches.
 */
import { useEffect } from 'react'
import {
  createTheme, alpha, getContrastRatio, lighten, darken, type Theme,
} from '@mui/material/styles'
import {
  brandActiveSource, brandBadgeSource, brandBaseFontSize, brandButtonPrimarySource,
  brandButtonSecondarySource, brandFocusSource, brandFontSize, brandHeadings,
  brandFontWeightBody, brandFontWeightHeading, brandHighlightSource,
  brandLinkSource, brandNightMode, brandWebfonts,
  brandElevation, brandRadiusPx, brandFontWeightSet, fontFamily, headingFontFamily, primaryColor,
  brandSecondary, brandAccentResolved, brandLogoAlt, brandLogoUrl, brandLogoUrlDark,
  nightModeAllowed, resolveSourceColor,
  toHex, type BrandWebfont, type LvClientConfig,
} from './lvConfig'

// ── Custom palette tokens (the accent/highlight slots) ───────────────────────
declare module '@mui/material/styles' {
  interface Palette {
    /** The dedicated accent token (D5): badges, highlights, secondary-button
     *  semantics live HERE, not on palette.secondary. */
    accent: Palette['primary']
    /** The highlight-role token (brand.highlightSource). */
    highlight: Palette['primary']
  }
  interface PaletteOptions {
    accent?: PaletteOptions['primary']
    highlight?: PaletteOptions['primary']
  }
  interface Theme {
    /** The LV semantic token namespace (Phase 1, 2026-10-15): the resolved
     *  role colours, brand assets and appearance policy in one stable place.
     *  Emitted by buildLvTheme; a STOCK createTheme() has no lv — read it
     *  only from a theme the builder made. */
    lv: LvThemeNamespace
  }
  interface ThemeOptions {
    lv?: LvThemeNamespace
  }
}

/** Light/dark mode identifier (the same union palette.mode uses). */
export type LvMode = 'light' | 'dark'

/**
 * The theme.lv namespace (Phase 1 — additive, no consumer, no render change).
 * The semantic role colours, brand assets and appearance policy, projected
 * from the SAME resolved locals the palette and component overrides consume,
 * so a surface or story never re-derives a role colour (or reaches for a
 * palette slot whose owner moved in v2). Paths:
 *
 *   lv.semantic.link        lv.badge          lv.assets.logo.url
 *   lv.action.primary       lv.highlight      lv.assets.logo.darkUrl
 *   lv.action.secondary     lv.focus          lv.assets.logo.alt
 *   lv.navigation.active                      lv.appearance.allowedModes
 *                                             lv.appearance.defaultMode
 *                                             lv.appearance.canToggle
 */
export interface LvThemeNamespace {
  semantic: {
    /** Link text colour (brand.linkSource, default primary). */
    link: string
  }
  action: {
    /** Primary-button role (brand.buttonPrimarySource, default primary). */
    primary: string
    /** Secondary-button role (brand.buttonSecondarySource, default accent). */
    secondary: string
  }
  navigation: {
    /** Active nav/tab/menu role (brand.activeSource, default primary) — the
     *  colour palette.action.selected carries at MUI's stock alpha. */
    active: string
  }
  /** Badge role (brand.badgeSource, default accent) — MuiChip/MuiBadge
   *  secondary slots. */
  badge: string
  /** Highlight role (brand.highlightSource, default accent) — the same hex
   *  palette.highlight.main carries. */
  highlight: string
  /** The mode-resolved :focus-visible ring colour (focus-role source floored
   *  at 3:1 against the mode background) — the value MuiCssBaseline emits. */
  focus: string
  assets: {
    logo: {
      /** brand.logoUrl; undefined when the deployment has no logo. */
      url?: string
      /** brand.logoUrlDark, falling back to url; undefined when no logo. */
      darkUrl?: string
      /** brand.logoAlt, falling back to the product name (never empty). */
      alt: string
    }
  }
  appearance: {
    /** The modes the deployment permits: ['light'] when the nightMode policy
     *  is off, ['light','dark'] otherwise. */
    allowedModes: LvMode[]
    /** The POLICY default mode (dark policy → dark, everything else light).
     *  The learner's session choice can override it — the built theme's mode
     *  is palette.mode; this records the policy, not the choice. */
    defaultMode: LvMode
    /** Whether the learner is offered the light/dark choice at all. */
    canToggle: boolean
  }
}

/** Semantic font sizes (D6 review candidates — the customer contract is
 *  small/medium/large; the px mapping is reviewed in Storybook). */
const FONT_SIZE_PX = { small: 13, medium: 14, large: 16 } as const

/** Focus ring system tokens (fixed, accessibility-owned — not brand knobs). */
const FOCUS_RING_WIDTH = 2
const FOCUS_RING_OFFSET = 2
/** The focus colour must clear this contrast ratio against the surface
 *  behind it (WCAG non-text contrast). */
const FOCUS_CONTRAST_FLOOR = 3

/** Resolve the focus colour for this mode, adjusted up the deterministic
 *  ladder until it clears the 3:1 floor against the mode's default
 *  background (dark brands on dark surfaces would otherwise vanish).
 *  Output is normalized to #rrggbb (the contract's colour shape). */
export function focusRingColor(sourceHex: string, backgroundHex: string): string {
  if (getContrastRatio(sourceHex, backgroundHex) >= FOCUS_CONTRAST_FLOOR) return toHex(sourceHex)
  for (const candidate of [
    lighten(sourceHex, 0.2), darken(sourceHex, 0.2),
    lighten(sourceHex, 0.4), darken(sourceHex, 0.4),
    lighten(sourceHex, 0.6),
  ]) {
    if (getContrastRatio(candidate, backgroundHex) >= FOCUS_CONTRAST_FLOOR) return toHex(candidate)
  }
  // Nothing on the ladder clears it: black/white, whichever contrasts more.
  return getContrastRatio('#000000', backgroundHex) >= getContrastRatio('#ffffff', backgroundHex)
    ? '#000000'
    : '#ffffff'
}

/** MUI's contrastText recipe (createPalette.js getContrastText): white when
 *  the ratio against the colour clears the 3 threshold, near-black else. */
function contrastTextFor(hex: string): string {
  return getContrastRatio(hex, '#fff') >= 3 ? '#fff' : 'rgba(0, 0, 0, 0.87)'
}

/** The elevation recipes (D6 review candidates): flat removes all shadows;
 *  subtle is MUI's stock set; elevated lifts every level by one (the top
 *  level repeats itself). */
function elevationShadows(recipe: 'flat' | 'subtle' | 'elevated', stock: string[]): string[] | undefined {
  if (recipe === 'subtle') return undefined // stock — emit nothing
  if (recipe === 'flat') return stock.map(() => 'none')
  return stock.map((s, i) => (i === 0 ? s : stock[Math.min(i + 1, stock.length - 1)]))
}

export function buildLvTheme(cfg: LvClientConfig | null, mode: 'light' | 'dark'): Theme {
  // ── Stage 1: the brand palette (stored or derived — resolved upstream) ──
  const primaryHex = primaryColor(cfg)
  const secondaryHex = brandSecondary(cfg)
  const accentHex = brandAccentResolved(cfg)
  const highlightHex = resolveSourceColor(cfg, brandHighlightSource(cfg))

  const radius = brandRadiusPx(cfg)
  const body = fontFamily(cfg)
  const heading = headingFontFamily(cfg)
  // v3: the accessible px (brand.baseFontSize) wins over the semantic
  // small/medium/large mapping when both are stored.
  const fontSizePx = brandBaseFontSize(cfg) ?? FONT_SIZE_PX[brandFontSize(cfg)]
  const bodyWeight = brandFontWeightBody(cfg)
  // Heading weight 600 is the ratified default (Isobel, 2026-10-06): admins
  // tune it via brand.fontWeightHeading once the Branding interface ships.
  // Applied to every h1–h6 — the change from MUI's per-variant stock weights
  // (300/300/400/400/400/500) is an intentional expected delta in the
  // parity manifest, not a regression. v3: brand.headings overrides size
  // AND weight per level for h1–h4 (h5/h6 always take the heading-wide
  // defaults); unset levels keep stock MUI sizing.
  const headingWeight = brandFontWeightHeading(cfg)
  const perHeading = brandHeadings(cfg) ?? {}

  // Role colours (defaults reproduce the pre-expansion rendering).
  const linkHex = resolveSourceColor(cfg, brandLinkSource(cfg))
  const buttonPrimaryHex = resolveSourceColor(cfg, brandButtonPrimarySource(cfg))
  const buttonSecondaryHex = resolveSourceColor(cfg, brandButtonSecondarySource(cfg))
  const activeHex = resolveSourceColor(cfg, brandActiveSource(cfg))
  const badgeHex = resolveSourceColor(cfg, brandBadgeSource(cfg))
  // The focus ring colour, resolved ONCE per build: the MuiCssBaseline
  // override and the theme.lv namespace below consume the same value.
  const focusHex = focusRingColor(
    resolveSourceColor(cfg, brandFocusSource(cfg)),
    mode === 'dark' ? '#121212' : '#ffffff',
  )

  // ── Stage 2: the mode-aware palette ──────────────────────────────────────
  const base = createTheme({
    shape: { borderRadius: radius ?? 8 },
    palette: {
      mode,
      // Dark mode follows MUI's own convention: the primary LIGHTENS for
      // on-dark text/outlined usage — the light-mode hex on #121212 measured
      // 1.61:1 (axe, Phase-3 rollout 2026-10-19). lighten(0.4) lands ≥5:1
      // while keeping the brand hue; contained fills flip contrastText
      // automatically via augmentColor.
      primary: { main: mode === 'dark' ? lighten(primaryHex, 0.4) : primaryHex },
      secondary: { main: secondaryHex },
      // Light-mode semantic slots: MUI's stock info/warning mains miss 4.5:1
      // with white small text (3.85:1 / 3.11:1 — axe, same audit); the dark
      // stock already passes, so dark keeps it.
      ...(mode === 'light'
        ? { info: { main: '#0277bd' }, warning: { main: '#c24100' } }
        : {}),
      // action.selected: the active-role colour tinted at MUI's stock
      // opacities (0.08 light / 0.16 dark) — the tab strip, menus and lists
      // all read this slot.
      action: { selected: alpha(activeHex, mode === 'dark' ? 0.16 : 0.08) },
    },
    typography: {
      fontSize: fontSizePx,
      fontWeightRegular: bodyWeight,
      ...(body ? { fontFamily: body } : {}),
      // h1–h6: the heading font (when picked) + the ratified 600 default
      // weight (admin-tunable via brand.fontWeightHeading).
      ...Object.fromEntries(
        ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].map((v) => {
          const style = (perHeading as Record<string, { fontSize?: number; fontWeight?: number } | undefined>)[v]
          return [
            v,
            {
              fontWeight: style?.fontWeight ?? headingWeight,
              ...(style?.fontSize ? { fontSize: style.fontSize } : {}),
              ...(heading ? { fontFamily: heading } : {}),
            },
          ]
        }),
      ),
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': { colorScheme: mode },
          // ── Stage 3: the focus ring (system contract: width/offset fixed,
          // colour = focus-role source floored at 3:1 against the mode's
          // background). New in v2 — an expected parity delta everywhere.
          '*:focus-visible': {
            outline: `${FOCUS_RING_WIDTH}px solid ${focusHex}`,
            outlineOffset: FOCUS_RING_OFFSET,
          },
        },
      },
      // ── UI-role mappings, emitted ONLY when the role's resolved colour
      // differs from what stock MUI renders (conditional emission = parity).
      ...(linkHex !== primaryHex
        ? {
            MuiLink: {
              variants: [{ props: { color: 'primary' }, style: { color: linkHex } }],
            },
          }
        : {}),
      // Buttons: the PRIMARY-button role remaps the *Primary slots when it
      // isn't the brand primary; the SECONDARY-button role defaults to the
      // ACCENT token (D5) and remaps the *Secondary slots whenever that
      // differs from the new palette.secondary — i.e. stock color="secondary"
      // buttons keep rendering exactly the accent they render today.
      ...(() => {
        const styleOverrides: Record<string, unknown> = {}
        if (buttonPrimaryHex !== primaryHex) {
          styleOverrides.textPrimary = { color: buttonPrimaryHex }
          styleOverrides.outlinedPrimary = { color: buttonPrimaryHex, borderColor: alpha(buttonPrimaryHex, 0.5) }
          styleOverrides.containedPrimary = {
            backgroundColor: buttonPrimaryHex,
            color: contrastTextFor(buttonPrimaryHex),
            '&:hover': { backgroundColor: darken(buttonPrimaryHex, 0.3) },
          }
        }
        if (buttonSecondaryHex !== secondaryHex) {
          styleOverrides.textSecondary = { color: buttonSecondaryHex }
          styleOverrides.outlinedSecondary = { color: buttonSecondaryHex, borderColor: alpha(buttonSecondaryHex, 0.5) }
          styleOverrides.containedSecondary = {
            backgroundColor: buttonSecondaryHex,
            color: contrastTextFor(buttonSecondaryHex),
            '&:hover': { backgroundColor: darken(buttonSecondaryHex, 0.3) },
          }
        }
        return Object.keys(styleOverrides).length ? { MuiButton: { styleOverrides } } : {}
      })(),
      // The BADGE role (chips + status badges) defaults to the accent token.
      ...(badgeHex !== secondaryHex
        ? {
            MuiChip: {
              styleOverrides: {
                colorSecondary: { backgroundColor: badgeHex, color: contrastTextFor(badgeHex) },
              },
            },
            MuiBadge: {
              styleOverrides: {
                colorSecondary: { backgroundColor: badgeHex, color: contrastTextFor(badgeHex) },
              },
            },
          }
        : {}),
    },
  })

  // The elevation recipe rides the stock shadow set (only flat/elevated
  // change anything; subtle IS stock).
  const shadows = elevationShadows(brandElevation(cfg), base.shadows)
  const theme = shadows ? createTheme(base, { shadows }) : base

  // The custom tokens are augmented POST-build with MUI's own recipe
  // (theme.palette.augmentColor) so light/dark/contrastText are computed
  // byte-identically to the stock slots — Stage 3's contrastText treatment.
  theme.palette.accent = theme.palette.augmentColor({ color: { main: accentHex }, name: 'accent' })
  theme.palette.highlight = theme.palette.augmentColor({ color: { main: highlightHex }, name: 'highlight' })

  // ── The theme.lv namespace (Phase 1): the resolved role colours, brand
  // assets and appearance policy, projected from the SAME locals the palette
  // and overrides just consumed. Additive — no consumer, no render change;
  // the parity harness excludes it from the golden diff by contract and
  // brandTheme.lv.test.ts pins its exact contents.
  theme.lv = {
    semantic: { link: linkHex },
    action: { primary: buttonPrimaryHex, secondary: buttonSecondaryHex },
    navigation: { active: activeHex },
    badge: badgeHex,
    highlight: highlightHex,
    focus: focusHex,
    assets: {
      logo: {
        url: brandLogoUrl(cfg),
        darkUrl: brandLogoUrlDark(cfg),
        alt: brandLogoAlt(cfg),
      },
    },
    appearance: {
      allowedModes: nightModeAllowed(cfg) ? ['light', 'dark'] : ['light'],
      defaultMode: brandNightMode(cfg) === 'dark' ? 'dark' : 'light',
      canToggle: nightModeAllowed(cfg),
    },
  }

  return theme
}

/** Side effects for the resolved brand: webfont loading + favicon. Call once
 *  per themed root (LvShell, LoginGate); everything is derived from cfg and
 *  cleaned up on unmount / config change. */
export function useBrandAssets(cfg: LvClientConfig | null): void {
  const fonts = brandWebfonts(cfg)
  const fontsKey = fonts.map((f) => (f.kind === 'google' ? `g:${f.family}` : `c:${f.family}:${f.url}`)).join(',')
  useEffect(() => {
    if (!fonts.length) return
    const cleanups: (() => void)[] = []

    // Google families: one combined stylesheet (display=swap), requesting
    // exactly the weights the resolved brand can render — the stock UI set
    // plus the stored body/heading weights and every per-header override
    // (v3 headings permit 800; a hardcoded set would faux-bold them) — each
    // in BOTH normal and italic (Isobel, 2026-10-09), so markdown <em> gets
    // the family's genuine oblique face instead of a synthesized skew. The
    // css2 API wants its tuples grouped by axis: all uprights, then italics.
    const google = fonts.filter((f) => f.kind === 'google')
    if (google.length) {
      const weights = brandFontWeightSet(cfg)
      const axis = `ital,wght@${[
        ...weights.map((w) => `0,${w}`),
        ...weights.map((w) => `1,${w}`),
      ].join(';')}`
      const href =
        'https://fonts.googleapis.com/css2?' +
        google.map((f) => `family=${encodeURIComponent(f.family).replace(/%20/g, '+')}:${axis}`).join('&') +
        '&display=swap'
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = href
      link.dataset.lvBrand = 'fonts'
      document.head.appendChild(link)
      cleanups.push(() => link.remove())
    }

    // Uploaded custom fonts: one @font-face per family (family/url are
    // contract-sanitized — no CSS metacharacters can reach this string).
    const custom = fonts.filter((f): f is Extract<BrandWebfont, { kind: 'custom' }> => f.kind === 'custom')
    if (custom.length) {
      const formatFor = (url: string) =>
        /\.woff2(\?|#|$)/i.test(url) ? 'woff2'
          : /\.woff(\?|#|$)/i.test(url) ? 'woff'
            : /\.otf(\?|#|$)/i.test(url) ? 'opentype'
              : /\.ttf(\?|#|$)/i.test(url) ? 'truetype' : 'woff2'
      const style = document.createElement('style')
      style.dataset.lvBrand = 'fonts-custom'
      style.textContent = custom.map((f) =>
        `@font-face { font-family: '${f.family}'; src: url('${f.url}') format('${formatFor(f.url)}'); font-display: swap; }`,
      ).join('\n')
      document.head.appendChild(style)
      cleanups.push(() => style.remove())
    }

    return () => { for (const undo of cleanups) undo() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fontsKey])

  const logoUrl = brandLogoUrl(cfg)
  useEffect(() => {
    if (!logoUrl) return
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    const created = !link
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    const prev = link.href
    link.href = logoUrl
    return () => {
      if (created) link!.remove()
      else link!.href = prev
    }
  }, [logoUrl])
}
